import classNames from "classnames";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { openFeedsFiltersOverlay } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { setFeedOrder } from "web/redux/ducks/feed-filters";
import { FeedSidebarSlice } from "web/redux/ducks/feed-sidebar";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { BannerSize } from "web/script/utils/app-banner-campaign";
import styles from "./feed-controls.css";

const AnalyticsActions = {
    newest: "SORT_BY_NEWEST",
    price_desc: "SORT_BY_HIGH_LOW",
    price_asc: "SORT_BY_LOW_HIGH",
    recommended: "SORT_BY_RECOMMENDED",
    best_sellers: "SORT_BY_BEST_SELLERS",
};

interface ControlState extends FeedSidebarSlice {
    sortID: string | undefined;
    topBanner: {
        size: BannerSize;
        isVisible: boolean;
        bannerType: "email_banner" | "app_banner";
    };
}

function extractControlState(state: ReduxStoreState): ControlState {
    return {
        ...state.feedSidebarReducer,
        sortID: state.feedFiltersReducer.active["view"],
        topBanner: {
            size: state.topBannerReducer.bannerSize,
            isVisible: state.topBannerReducer.isVisible,
            bannerType: state.topBannerReducer.bannerType,
        },
    };
}

interface SortOption {
    label: string;
    value: string;
    selected: boolean;
}

interface FeedControlProps {
    sortOptions: SortOption[];
}

export default function FeedControls({ sortOptions }: FeedControlProps): JSX.Element {
    const { count, loading, sortID, topBanner } = useReduxSelector(extractControlState);
    const dispatch = useDispatch();

    useEffect(() => {
        const feedControlsNode = document.querySelectorAll(
            ".lyst-main div[data-hypernova-key=FeedControls]"
        )[0];
        if (topBanner.isVisible) {
            if (topBanner.size === BannerSize.Small || topBanner.bannerType === "email_banner") {
                feedControlsNode.classList.add(styles.belowSmallAppBanner);
            }
        } else {
            feedControlsNode.classList.remove(styles.belowSmallAppBanner);
        }
    }, [topBanner]);

    const selected = useMemo(() => {
        for (let option of sortOptions) {
            if (typeof sortID == "undefined" && option.selected) {
                return option;
            } else if (option.value == sortID) {
                return option;
            }
        }
        // Fallback for when bad things happen
        return sortOptions[0];
    }, [sortID, sortOptions]);

    function onClickFilters(): void {
        openFeedsFiltersOverlay();
    }

    function onSortChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        let sortBy = event.target.value;
        analytics.event("sort_by", AnalyticsActions[sortBy]);
        dispatch(setFeedOrder(sortBy));
    }

    let whiteBackground = environment.getFeature("uj_background_color_switch");

    return (
        <div
            className={classNames(styles.buttonBar, {
                [styles.loading]: loading,
                [styles.white]: whiteBackground,
            })}
        >
            <div className={styles.sortWrapper}>
                <select defaultValue={selected.value} onChange={onSortChange} disabled={loading}>
                    {sortOptions.map(({ value, label }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
                <span className={styles.sortLabelDesktop}>
                    {selected.label}
                    <SVGIcon name="round-select-marker" className={styles.svgIcon} />
                </span>
                <span className={styles.sortLabelMobile}>
                    <SVGIcon name="sort" className={styles.svgIcon} />
                    {gettext("feed.sort.mobile_btn_text")}
                </span>
            </div>
            <button className={styles.filterButton} onClick={onClickFilters} disabled={loading}>
                <SVGIcon name="filters" className={styles.svgIcon} />
                {gettext("feed.filter.mobile_btn_text")}
                <span
                    className={classNames(styles.appliedCount, {
                        [styles.appliedCountVisible]: count && count > 0,
                    })}
                >
                    {count}
                </span>
            </button>
        </div>
    );
}
